import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { AgentAssistantRequestParamsSchema, AgentAssistantRequestParams, type FileSelectionTarget } from './schemas'
import { getStandardFederalLRRSources } from '@/jurisdiction-mappings/jurisdiction-maps'

export type AgentConversationFormData = {
  requestParams: AgentAssistantRequestParams
  webSearchEnabled?: boolean
}

/** An upload that is in flight: tracked by its client id, and which slot it will land in, until the server id arrives. */
export type PendingUpload = { clientTmpId: string; target: FileSelectionTarget }

export type AgentConversationFormState = {
  forms: Partial<{ [conversationId: string]: AgentConversationFormData }>
  pendingUploadsByConversationId: Partial<Record<string, PendingUpload[]>>
  attachedPendingUploadsByConversationId: Partial<Record<string, PendingUpload[]>>
  /* Unsent text in each conversation's composer. */
  inputTextByConversationId: Partial<Record<string, string>>
}

const initialState: AgentConversationFormState = {
  forms: {},
  pendingUploadsByConversationId: {},
  attachedPendingUploadsByConversationId: {},
  inputTextByConversationId: {},
}

// Create a new form with default values
const createNewForm = (): AgentConversationFormData => ({
  requestParams: AgentAssistantRequestParamsSchema.parse({}), // Use schema to apply defaults
  webSearchEnabled: true,
})

const setUserFilesRag = (requestParams: AgentAssistantRequestParams, userFileIds: string[]) => {
  const dedupedIds = Array.from(new Set(userFileIds))
  requestParams.user_files_rag = { user_file_ids: dedupedIds }
}

const setWritingSampleFiles = (requestParams: AgentAssistantRequestParams, userFileIds: string[]) => {
  const dedupedIds = Array.from(new Set(userFileIds))
  requestParams.writing_sample_files = { user_file_ids: dedupedIds }
}

// Write back a per-conversation upload list, dropping the conversation entry once it empties.
const setPendingUploads = (map: Partial<Record<string, PendingUpload[]>>, conversationId: string, uploads: PendingUpload[]) => {
  if (uploads.length === 0) {
    delete map[conversationId]
  } else {
    map[conversationId] = uploads
  }
}

// Add a pending upload to a per-conversation map, de-duped by client id.
const addUploadToMap = (map: Partial<Record<string, PendingUpload[]>>, conversationId: string, upload: PendingUpload) => {
  const uploads = map[conversationId] ?? []
  if (uploads.some((existing) => existing.clientTmpId === upload.clientTmpId)) return
  setPendingUploads(map, conversationId, [...uploads, upload])
}

// Remove a pending upload from a per-conversation map by client id.
const removeUploadFromMap = (map: Partial<Record<string, PendingUpload[]>>, conversationId: string, clientTmpId: string) => {
  setPendingUploads(
    map,
    conversationId,
    (map[conversationId] ?? []).filter((upload) => upload.clientTmpId !== clientTmpId)
  )
}

// Detach in-flight user-file uploads from the form (e.g. when the user switches the file source).
// Writing-sample uploads live in their own request field, so they are left attached and can still resolve.
const detachUserFilePendingUploads = (state: AgentConversationFormState, conversationId: string) => {
  setPendingUploads(
    state.attachedPendingUploadsByConversationId,
    conversationId,
    (state.attachedPendingUploadsByConversationId[conversationId] ?? []).filter((upload) => upload.target !== 'user-files')
  )
}

// Helper to validate the form or initialize it with default values
const validateOrInitializeForm = (state: AgentConversationFormState, conversationId: string): AgentConversationFormData => {
  const form = state.forms[conversationId]

  if (!form) {
    const newForm = createNewForm()
    state.forms[conversationId] = newForm
    return newForm
  }

  const validation = AgentAssistantRequestParamsSchema.safeParse(form.requestParams)

  if (!validation.success) {
    const newForm = createNewForm()
    state.forms[conversationId] = newForm
    return newForm
  }

  return form
}

/**
 * Agent Conversations Form Slice
 */
export const agentConversationFormSlice = createSlice({
  name: 'agentConversationFormState',
  initialState,
  reducers: {
    // ============== Slice Actions ============== >
    nullifyData: () => initialState,

    setWebSearchEnabled: (state, action: PayloadAction<{ conversationId: string; enabled: boolean }>) => {
      const { conversationId, enabled } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      form.webSearchEnabled = enabled
    },

    // Set the unsent composer text, dropping the conversation entry once it's empty.
    setInputText: (state, action: PayloadAction<{ conversationId: string; text: string }>) => {
      const { conversationId, text } = action.payload
      if (text) {
        state.inputTextByConversationId[conversationId] = text
      } else {
        delete state.inputTextByConversationId[conversationId]
      }
    },

    // Empty the composer after a send, unless the text changed while that send was being prepared.
    clearSubmittedInputText: (state, action: PayloadAction<{ conversationId: string; submittedText: string }>) => {
      const { conversationId, submittedText } = action.payload
      if ((state.inputTextByConversationId[conversationId] ?? '') === submittedText) delete state.inputTextByConversationId[conversationId]
    },

    // Set all selected user file ids.
    setUserFiles: (state, action: PayloadAction<{ conversationId: string; userFileIds: string[] }>) => {
      const { conversationId, userFileIds } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      setUserFilesRag(form.requestParams, userFileIds)
      detachUserFilePendingUploads(state, conversationId)
    },

    // Add a user file id to the form.
    addUserFile: (state, action: PayloadAction<{ conversationId: string; userFileId: string }>) => {
      const { conversationId, userFileId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      const existingIds = form.requestParams.user_files_rag?.user_file_ids ?? []
      setUserFilesRag(form.requestParams, [...existingIds, userFileId])
    },

    // Remove a user file id from the form.
    removeUserFile: (state, action: PayloadAction<{ conversationId: string; userFileId: string }>) => {
      const { conversationId, userFileId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      const existingIds = form.requestParams.user_files_rag?.user_file_ids ?? []
      setUserFilesRag(
        form.requestParams,
        existingIds.filter((id) => id !== userFileId)
      )
    },

    // Clear all selected user file ids.
    clearUserFiles: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      form.requestParams.user_files_rag = { user_file_ids: [] }
      detachUserFilePendingUploads(state, conversationId)
    },

    // A turn was sent with these files, so they leave the composer.
    removeSentFiles: (state, action: PayloadAction<{ conversationId: string; fileIds: string[]; writingSampleFileId: string | null }>) => {
      const { conversationId, fileIds, writingSampleFileId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      const sentFileIds = new Set(fileIds)
      setUserFilesRag(
        form.requestParams,
        form.requestParams.user_files_rag.user_file_ids.filter((id) => !sentFileIds.has(id))
      )
      setWritingSampleFiles(
        form.requestParams,
        form.requestParams.writing_sample_files.user_file_ids.filter((id) => id !== writingSampleFileId)
      )
    },

    // Deleted files are never sent, so they leave the composer along with the files a turn did send
    removeDeletedFiles: (state, action: PayloadAction<{ conversationId: string; deletedFileIds: string[] }>) => {
      const { conversationId, deletedFileIds } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      const deletedFileIdSet = new Set(deletedFileIds)
      setUserFilesRag(
        form.requestParams,
        form.requestParams.user_files_rag.user_file_ids.filter((id) => !deletedFileIdSet.has(id))
      )
      setWritingSampleFiles(
        form.requestParams,
        form.requestParams.writing_sample_files.user_file_ids.filter((id) => !deletedFileIdSet.has(id))
      )
    },

    addPendingUserFileUpload: (state, action: PayloadAction<{ conversationId: string; clientTmpId: string; target: FileSelectionTarget }>) => {
      const { conversationId, clientTmpId, target } = action.payload
      validateOrInitializeForm(state, conversationId)

      addUploadToMap(state.pendingUploadsByConversationId, conversationId, { clientTmpId, target })
      addUploadToMap(state.attachedPendingUploadsByConversationId, conversationId, { clientTmpId, target })
    },

    removePendingUserFileUpload: (state, action: PayloadAction<{ conversationId: string; clientTmpId: string }>) => {
      const { conversationId, clientTmpId } = action.payload
      removeUploadFromMap(state.pendingUploadsByConversationId, conversationId, clientTmpId)
      removeUploadFromMap(state.attachedPendingUploadsByConversationId, conversationId, clientTmpId)
    },

    // Add a writing sample by user_file id.
    addWritingSampleFile: (state, action: PayloadAction<{ conversationId: string; userFileId: string }>) => {
      const { conversationId, userFileId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)

      // Restrict to 1 writing sample.
      setWritingSampleFiles(form.requestParams, [userFileId])
    },

    // Remove a writing sample by user_file id.
    removeWritingSampleFile: (state, action: PayloadAction<{ conversationId: string; userFileId: string }>) => {
      const { conversationId, userFileId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      const existingIds = form.requestParams.writing_sample_files?.user_file_ids ?? []
      setWritingSampleFiles(
        form.requestParams,
        existingIds.filter((id) => id !== userFileId)
      )
    },

    // Clear writing samples from the form.
    clearWritingSampleFiles: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)
      form.requestParams.writing_sample_files = { user_file_ids: [] }
    },

    // Set include unpublished cases
    setIncludeUnpublishedCases: (state, action: PayloadAction<{ conversationId: string; includeUnpublishedCases: boolean }>) => {
      const { conversationId, includeUnpublishedCases } = action.payload

      const form = validateOrInitializeForm(state, conversationId)
      if (form.requestParams.caselaw_search_filter) {
        form.requestParams.caselaw_search_filter.include_unpublished_cases = includeUnpublishedCases
      } else {
        form.requestParams.caselaw_search_filter = {
          jurisdiction_codes: [],
          include_unpublished_cases: includeUnpublishedCases,
        }
      }
    },

    // Clear research jurisdiction codes from request params (keeps UI filters intact for later restore)
    clearResearchJurisdictions: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      const form = validateOrInitializeForm(state, conversationId)

      if (form.requestParams.caselaw_search_filter) {
        form.requestParams.caselaw_search_filter.jurisdiction_codes = []
      }
      if (form.requestParams.lrr_search_filter) {
        form.requestParams.lrr_search_filter.jurisdiction_codes = []
      }
    },

    // Set caselaw date range
    setCaselawDateRange: (state, action: PayloadAction<{ conversationId: string; dateLow: string | null; dateHigh: string | null }>) => {
      const { conversationId, dateLow, dateHigh } = action.payload

      const form = validateOrInitializeForm(state, conversationId)
      if (form.requestParams.caselaw_search_filter) {
        form.requestParams.caselaw_search_filter.date_low = dateLow
        form.requestParams.caselaw_search_filter.date_high = dateHigh
      } else {
        form.requestParams.caselaw_search_filter = {
          jurisdiction_codes: [],
          date_low: dateLow,
          date_high: dateHigh,
          include_unpublished_cases: false,
        }
      }
    },

    // Sync research filters to request params
    // This action reads from researchFiltersState and updates the requestParams
    // It should be dispatched after any change to the research filters UI state
    syncResearchFiltersToRequestParams: (
      state,
      action: PayloadAction<{
        conversationId: string
        stateLrrSources: string[]
        stateCaselawSources: string[]
        federalLrrSources: string[]
        federalCaselawSources: string[]
      }>
    ) => {
      const { conversationId, stateLrrSources, stateCaselawSources, federalLrrSources, federalCaselawSources } = action.payload
      const form = validateOrInitializeForm(state, conversationId)

      // Check if any federal content is selected
      const hasFederalContent = federalLrrSources.length > 0 || federalCaselawSources.length > 0

      // If federal content is selected, automatically include standard federal LRR sources
      // These sources don't appear in the UI, only in the payload
      const federalLrrToInclude = hasFederalContent ? Array.from(new Set([...federalLrrSources, ...getStandardFederalLRRSources()])) : federalLrrSources

      // Combine state and federal sources
      const allLrrSources = Array.from(new Set([...stateLrrSources, ...federalLrrToInclude]))
      const allCaselawSources = Array.from(new Set([...stateCaselawSources, ...federalCaselawSources]))

      // Update LRR search filter
      form.requestParams.lrr_search_filter = {
        jurisdiction_codes: allLrrSources,
      }

      // Update caselaw search filter (preserve other properties)
      const existingCaselawFilter = form.requestParams.caselaw_search_filter || {
        jurisdiction_codes: [],
        include_unpublished_cases: false,
      }
      form.requestParams.caselaw_search_filter = {
        ...existingCaselawFilter,
        jurisdiction_codes: allCaselawSources,
      }
    },
  },
})

// Actions
export const AgentConversationFormActions = agentConversationFormSlice.actions

export default agentConversationFormSlice.reducer
